import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { VenueFormGroup } from "../VenueFormGroup/VenueFormGroup.tsx";
import { ChildVenue } from "../../types/dataTypes.ts";
import { ArrowForward, FilterList } from "@mui/icons-material";

interface VenueModalButtonProps {
  venues: ChildVenue[];
  selectedVenues: string[];
  onVenueToggle: (clickedVenue: string) => void;
  buttonLabel?: string;
  dialogTitle?: string;
  onDialogClose: () => void;
  setIsVenueModalOpen: (isOpen: boolean) => void;
  toggleSelectAllVenues: (selectAll: boolean) => void;
  selectAllVenuesLabel: string;
  venueDistanceLabel: string;
}

/**
 * A convenience wrapper that renders a button; clicking it opens a modal
 * containing the {@link VenueFormGroup} component. The modal is responsive
 * (full‑screen on phones) and provides a close icon plus a footer action.
 */
export const VenueModalButton: React.FC<VenueModalButtonProps> = ({
  venues,
  selectedVenues,
  onVenueToggle,
  buttonLabel = "Select Cinemas",
  dialogTitle = "Select Cinemas",
  onDialogClose,
  setIsVenueModalOpen,
  toggleSelectAllVenues,
  selectAllVenuesLabel,
  venueDistanceLabel,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = () => {
    setOpen(true);
    setIsVenueModalOpen(true);
  };
  const handleClose = () => {
    onDialogClose();
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        color="primary"
        sx={{
          fontSize: { xs: "large", md: "x-large" },
          width: { xs: "90%", md: "50%" },
          borderRadius: "10px",
          boxShadow: "0 0 12px 6px rgba(25,118,210,0.65)",
          letterSpacing: 0.4,
          fontWeight: "bold",
          // py: 2,
        }}
      >
        {buttonLabel}
        <FilterList sx={{ fontSize: "1.3em", ml: 0.5 }} />
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        keepMounted
        disableScrollLock
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            fontWeight: 600,
            fontSize: "xx-large",
            textAlign: "center",
            textDecoration: "underline",
          }}
        >
          {dialogTitle}
          <IconButton
            // ariaLabel="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <VenueFormGroup
            venues={venues}
            selectedVenues={selectedVenues}
            onVenueToggle={onVenueToggle}
            toggleSelectAllVenues={toggleSelectAllVenues}
            selectAllVenuesLabel={selectAllVenuesLabel}
            venueDistanceLabel={venueDistanceLabel}
          />
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              fontSize: { xs: "1.2rem", md: "2rem" },
              borderRadius: "10px",
              boxShadow: "0 0 12px 6px rgba(25,118,210,0.65)",
              letterSpacing: 0.4,
              fontWeight: "bold",
            }}
          >
            Select Cinemas
            <ArrowForward sx={{ fontSize: "1em", ml: 0.5 }} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VenueModalButton;
